import { useCallback, useEffect, useState } from 'react';

import {
  fetchWorkspaceEmailTemplates,
  openResetTemplateDialog,
  openTemplateEditor,
} from '../../store/workspaceEmailTemplatesSlice';

import EmailTemplateEditorModal from './EmailTemplateEditorModal';
import EmailTemplateRow from './EmailTemplateRow';
import ResetEmailTemplateDialog from './ResetEmailTemplateDialog';

import { useAppDispatch, useAppSelector } from '@store/hooks';

interface Props {
  tenantId: string;
}

const WorkspaceEmailTemplatesView: React.FC<Props> = ({ tenantId }) => {
  const dispatch = useAppDispatch();
  const { list, listStatus, listError } = useAppSelector(
    (s) => s.settingsWorkspaceEmailTemplates
  );

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchWorkspaceEmailTemplates(tenantId));
  }, [dispatch, tenantId]);

  const handleRetry = useCallback(() => {
    dispatch(fetchWorkspaceEmailTemplates(tenantId));
  }, [dispatch, tenantId]);

  const handleEdit = (templateType: string) => {
    dispatch(openTemplateEditor(templateType));
  };

  const handleReset = (templateType: string) => {
    dispatch(openResetTemplateDialog(templateType));
  };

  const handleSaved = useCallback(() => {
    setSuccessMessage('Template saved successfully.');
    dispatch(fetchWorkspaceEmailTemplates(tenantId));
    setTimeout(() => setSuccessMessage(null), 5000);
  }, [dispatch, tenantId]);

  const handleDeleted = useCallback(() => {
    setSuccessMessage('Template reset successfully.');
    dispatch(fetchWorkspaceEmailTemplates(tenantId));
    setTimeout(() => setSuccessMessage(null), 5000);
  }, [dispatch, tenantId]);

  return (
    <div className="w-full max-w-full overflow-hidden flex flex-col space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
      </div>

      {successMessage && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {successMessage}
        </div>
      )}

      {listStatus === 'loading' && (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-gray-500">
            Loading email templates...
          </span>
        </div>
      )}

      {listStatus === 'failed' && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <p>{listError}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="mt-2 inline-flex px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {listStatus === 'succeeded' && (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Template Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Source
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {list.map((item) => (
                <EmailTemplateRow
                  key={item.templateType}
                  item={item}
                  onEdit={handleEdit}
                  onReset={handleReset}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EmailTemplateEditorModal tenantId={tenantId} onSaved={handleSaved} />
      <ResetEmailTemplateDialog tenantId={tenantId} onDeleted={handleDeleted} />
    </div>
  );
};

export default WorkspaceEmailTemplatesView;
